import { useState } from "react";
import { formatPrice } from "@utils/money";
import { normalisePromoCode, PROMO_MIN_LABELS } from "@utils/promoCode";
import { useBasket } from "hooks/useBasket";

import BoxInput from "components/atoms/BoxInput";

const GiftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8h18v4H3z"/>
    <path d="M12 8v13"/>
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5C9 3 11 5 12 8c1-3 3-5 4.5-5a2.5 2.5 0 0 1 0 5"/>
  </svg>
);

/* Three steps towards the free label: 1, 2, gift. Filled steps are dark green (dpGreenDeep) so white content meets WCAG AA contrast. */
export const PromoTracker = () => {
  const { numLabels, promoCode, promoDiscount } = useBasket();

  if (!promoCode) return null;

  const filled = Math.min(numLabels, PROMO_MIN_LABELS);
  const remaining = PROMO_MIN_LABELS - filled;

  return <div className="mt-4 rounded-lg border-1 border-dpLightGray p-4">
    <div className="flex items-center mb-3" aria-hidden="true">
      {[ 1, 2, 3 ].map(step => {
        const done = step <= filled;
        return <div key={step} className="flex items-center">
          {step > 1 && <div className={`w-6 h-0.5 ${done ? "bg-dpGreenDeep" : "bg-dpLightGray"}`}/>}
          <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${done ? "bg-dpGreenDeep border-dpGreenDeep text-white" : "bg-white border-dpLightGray text-dpGrayDark"}`}>
            {step === PROMO_MIN_LABELS ? <GiftIcon/> : step}
          </div>
        </div>;
      })}
    </div>
    <p className="text-sm text-dpGreenDark" aria-live="polite">
      {
        promoDiscount
          ? <><b>Your cheapest label is free</b>, you saved {formatPrice(promoDiscount)}.</>
          : remaining === 1
            ? <b>One more parcel and your cheapest label is free.</b>
            : <>Add {remaining} more parcels and your cheapest label is free.</>
      }
    </p>
  </div>;
};

export const PromoCodeField = () => {
  const { promoCode, setPromoCode } = useBasket();
  const [ value, setValue ] = useState(promoCode);
  const [ error, setError ] = useState("");

  const apply = () => {
    if (!value.trim()) {
      setError("Enter a promo code first.");
      return;
    }
    const code = normalisePromoCode(value);
    if (!code) {
      setError("That code isn't valid. Check it and try again.");
      return;
    }
    setError("");
    setValue(code);
    setPromoCode(code);
  };

  const remove = () => {
    setError("");
    setValue("");
    setPromoCode("");
  };

  const saved = !!promoCode && normalisePromoCode(value) === promoCode;

  return <div className="mt-4">
    <label htmlFor="basket-promo-code" className="block font-bold text-dpGreenDark mb-1">Promo code</label>
    <div className="flex flex-row items-center gap-2">
      <BoxInput
        id="basket-promo-code"
        value={value}
        placeholder="Enter your code"
        autoComplete="off"
        onChange={e => {
          setValue(e.currentTarget.value);
          setError("");
        }}
        onKeyDown={e => e.key === "Enter" && apply()}
        aria-invalid={!!error}
        aria-describedby="basket-promo-code-status"
        className="w-40 uppercase placeholder:normal-case"
      />
      {/* One button that changes label, so it stays in the DOM when clicked and the basket modal's outside-click check doesn't close it */}
      <button
        onClick={saved ? remove : apply}
        className={saved
          ? "text-dpGreen font-bold text-sm hover:underline px-2 py-2"
          : "rounded-lg font-bold px-4 py-1.5 border-2 bg-gradient-to-t from-dpGreenDark to-dpGreen text-white border-dpGreenDark hover:underline"}
      >
        {saved ? "Remove" : "Apply"}
      </button>
    </div>
    {(error || saved) && <p id="basket-promo-code-status" className={`text-sm mt-1 ${error ? "text-dpRed" : "text-dpGreenDark"}`} role={error ? "alert" : undefined}>
      {error || <b>&#10003; {promoCode} applied</b>}
    </p>}
  </div>;
};

export const PromoDiscountLine = () => {
  const { promoDiscount } = useBasket();

  if (!promoDiscount) return null;

  return <div className="mt-4 font-bold text-dpGreenDark">
    Cheapest label free: -{formatPrice(promoDiscount)}
  </div>;
};
